import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  AlertCircle,
  Briefcase,
  Building2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  FileText,
  ListTodo,
  MapPin,
  MapPinned,
  Maximize2,
  Minimize2,
  Moon,
  Navigation,
  PhoneCall,
  Siren,
  ShoppingBasket,
  Sun,
  Truck,
  User2,
  X
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import MapCurrentLocationButton from './MapCurrentLocationButton';
import ModalShell from './ModalShell';
import { API_URL } from '../config';
import useModalBackButton from '../hooks/useModalBackButton';
import { prepareRoutesReturnHistory, saveRoutesNavigationContext } from '../utils/navigationContext';
import { haptic } from '../utils/vibration';
import useToast from '../hooks/useToast';
import { STAGES_CONFIG } from '../utils/stages';
import { CURRENT_LOCATION_MAP_ZOOM, replaceCurrentLocationMarker } from '../utils/mapCurrentLocation';
import { getYandexAddressUrl, getYandexMapsUrl, getYandexNavigatorUrl, loadYandexMaps, loadYandexMapsV3, suppressYandexOpenMapsButton } from '../utils/yandexMaps';
import { getDgisAddressUrl, getDgisRouteUrl } from '../utils/dgisMaps';
import { geocodeAddressWithNominatim } from '../utils/addressCoordinates';
import MapProviderToggle, { getStoredMapProvider } from './MapProviderToggle';
import yaMapsIcon from '../assets/images/ya_maps___.svg';
import yandexNavigatorIcon from '../assets/images/yandex_navigator_.svg';
import dgisIcon from '../assets/images/dgis_icon.svg';
import L from 'leaflet';
import 'leaflet.markercluster';

const STAGE_SHORT_LABELS = {
  pickup: 'От клиента',
  delivery: 'К клиенту',
  cleaning: 'Химчистка',
  task: 'Поруч.'
};
const TASK_STAGE_META = {
  label: 'Поручение',
  icon: ListTodo,
  color: 'indigo'
};
const DRAFT_ORDER_ROUTE_ID = '__draft_order__';
const DRAFT_ORDER_STAGE_META = {
  label: 'Новый заказ',
  icon: MapPin,
  color: 'red'
};

const isDraftRouteItem = (item) => item?.entity_type === 'draft_order' || item?.is_draft_order === true;
const getDraftRouteLabel = (item) => String(item?.draft_label || item?.draftLabel || DRAFT_ORDER_STAGE_META.label).trim() || DRAFT_ORDER_STAGE_META.label;

const hideRegionPrefix = (address = '') => String(address || '').replace(/^[^,]+?\s+обл,\s*/i, '').trim();
const formatTimeSlot = (value = '') => String(value || '').trim().replace(/\s*-\s*/g, '–');
const getTimeSlotBounds = (timeSlot = '') => {
  const matches = String(timeSlot || '').match(/(\d{1,2})[:.](\d{2})/g);
  if (!matches?.length) return null;

  const parsePart = (part) => {
    const [hours, minutes] = String(part).split(/[:.]/).map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    return { hours, minutes };
  };

  const start = parsePart(matches[0]);
  const end = parsePart(matches[matches.length - 1]);
  if (!start || !end) return null;
  return { start, end };
};

const getRouteScheduleColor = (item) => {
  if (isDraftRouteItem(item)) return 'draft';
  const dateKey = String(item?.scheduled_date_key || '').trim();
  const bounds = getTimeSlotBounds(item?.time_slot);
  if (!dateKey || !bounds) return 'blue';

  const now = new Date();
  const start = new Date(`${dateKey}T00:00:00`);
  start.setHours(bounds.start.hours, bounds.start.minutes, 0, 0);

  const end = new Date(`${dateKey}T00:00:00`);
  end.setHours(bounds.end.hours, bounds.end.minutes, 59, 999);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'blue';
  if (now > end) return 'red';
  if (now >= start && now <= end) return 'green';
  return 'blue';
};

const formatDateChip = (dateKey) => {
  if (!dateKey) return '';
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const formatRouteDateLabel = (dateKey) => {
  if (!dateKey) return '';
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    weekday: 'short'
  }).format(date).replace(',', '');
};

const formatCalendarDateKey = (date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

const ROUTES_MAP_CENTER_KEY = 'routes_map_center_v3';
const ROUTES_MAP_ZOOM_KEY = 'routes_map_zoom_v3';
const MAP_DEFAULT_ZOOM = 13;
const MAP_MIN_RESTORED_ZOOM = 11;
const MAP_CLUSTER_SELECTION_ZOOM = 15;
const MAP_ROUTE_FOCUS_ZOOM = 15;
const MAP_FALLBACK_CENTER = [37.6176, 55.7558];
const DEG_TO_RAD = Math.PI / 180;
const MAP_3D_TILT = 40 * DEG_TO_RAD;
const LEGACY_ROUTE_MARKER_ICON_SHAPE = {
  type: 'Rectangle',
  coordinates: [[-132, -48], [132, 14]]
};

const getClientTypeKind = (value = '') => (
  ['b2b', 'company', 'organization'].includes(String(value || '').toLowerCase()) ? 'company' : 'individual'
);

const getRouteStatusTone = (item) => {
  if (isDraftRouteItem(item)) return 'draft';
  const lifecycleStatus = String(item?.lifecycle_status || '').trim().toLowerCase();
  if (lifecycleStatus === 'cancelled') return 'red';
  if (lifecycleStatus === 'completed') return 'green';
  if (lifecycleStatus === 'revision_active') return 'orange';
  if (lifecycleStatus === 'unassigned') return 'gray';
  if (lifecycleStatus === 'active') return 'blue';

  if (item?.status === 'cancelled') return 'red';
  if (item?.status === 'completed') return 'green';
  if (item?.has_active_revision) return 'orange';
  if (!item?.assigned_to) return 'gray';
  return 'blue';
};

const ROUTE_STATUS_META = {
  blue: { label: 'Активные', dotClassName: 'bg-blue-500' },
  draft: { label: 'Новый заказ', dotClassName: 'bg-red-500' },
  orange: { label: 'Повтор', dotClassName: 'bg-orange-500' },
  gray: { label: 'Нераспределен', dotClassName: 'bg-slate-400' },
  green: { label: 'Выполнено', dotClassName: 'bg-emerald-500' },
  red: { label: 'Отменено', dotClassName: 'bg-red-500' }
};

const getRouteStatusMeta = (item) => {
  const tone = getRouteStatusTone(item);
  return {
    tone,
    ...ROUTE_STATUS_META[tone]
  };
};

const getRouteStatusBadgeClassName = (tone) => {
  if (tone === 'draft') return 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300';
  if (tone === 'orange') return 'bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-300';
  if (tone === 'gray') return 'bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300';
  if (tone === 'green') return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300';
  if (tone === 'red') return 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300';
  return 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300';
};

const getStageBadgeClassName = (color = 'gray') => {
  if (color === 'amber') return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-900/70';
  if (color === 'cyan') return 'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/20 dark:text-cyan-400 dark:border-cyan-900/70';
  if (color === 'rose') return 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-900/70';
  if (color === 'indigo') return 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-400 dark:border-indigo-900/70';
  if (color === 'purple') return 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-900/70';
  if (color === 'yellow') return 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-900/70';
  if (color === 'teal') return 'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-900/70';
  if (color === 'lime') return 'bg-lime-100 text-lime-700 border-lime-200 dark:bg-lime-900/20 dark:text-lime-400 dark:border-lime-900/70';
  if (color === 'fuchsia') return 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200 dark:bg-fuchsia-900/20 dark:text-fuchsia-400 dark:border-fuchsia-900/70';
  return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
};

const getSavedMapCenter = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(ROUTES_MAP_CENTER_KEY) || 'null');
    if (Array.isArray(parsed) && parsed.length === 2 && parsed.every((value) => Number.isFinite(Number(value)))) {
      return parsed.map(Number);
    }
  } catch {
    // no-op
  }
  return null;
};

const getSavedMapZoom = () => {
  const parsed = Number(localStorage.getItem(ROUTES_MAP_ZOOM_KEY));
  if (!Number.isFinite(parsed)) return null;
  return parsed >= MAP_MIN_RESTORED_ZOOM ? parsed : null;
};

const saveMapCenter = (center) => {
  try {
    localStorage.setItem(ROUTES_MAP_CENTER_KEY, JSON.stringify(center));
  } catch {
    // no-op
  }
};

const saveMapZoom = (zoom) => {
  try {
    localStorage.setItem(ROUTES_MAP_ZOOM_KEY, String(zoom));
  } catch {
    // no-op
  }
};

const getSafeMapZoom = (zoom, fallback = MAP_DEFAULT_ZOOM) => {
  const parsed = Number(zoom);
  if (!Number.isFinite(parsed) || parsed < MAP_MIN_RESTORED_ZOOM) return fallback;
  return parsed;
};

const hasValidCoords = (item) => {
  if (item?.latitude === null || item?.latitude === undefined || item?.longitude === null || item?.longitude === undefined) {
    return false;
  }

  const lat = Number(item.latitude);
  const lng = Number(item.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

const getStageMeta = (item) => {
  if (isDraftRouteItem(item)) {
    return {
      ...DRAFT_ORDER_STAGE_META,
      label: getDraftRouteLabel(item),
      shortLabel: String(item?.draft_short_label || item?.draftShortLabel || 'Новый').trim() || 'Новый',
      badgeClassName: getStageBadgeClassName(DRAFT_ORDER_STAGE_META.color)
    };
  }
  if (item?.entity_type === 'task' || item?.stage_name === 'task') {
    return {
      ...TASK_STAGE_META,
      shortLabel: STAGE_SHORT_LABELS.task,
      badgeClassName: getStageBadgeClassName(TASK_STAGE_META.color)
    };
  }
  const stageName = item?.stage_name || 'pickup';
  const config = STAGES_CONFIG[stageName] || STAGES_CONFIG.pickup;
  return {
    ...config,
    shortLabel: STAGE_SHORT_LABELS[stageName] || config.label,
    badgeClassName: getStageBadgeClassName(config.color)
  };
};

const getRouteMarkerStageIconSvg = (stageName, entityType = 'order') => {
  const Icon = entityType === 'draft_order'
    ? DRAFT_ORDER_STAGE_META.icon
    : entityType === 'task'
    ? TASK_STAGE_META.icon
    : (STAGES_CONFIG[stageName]?.icon || STAGES_CONFIG.pickup.icon);
  return renderToStaticMarkup(<Icon className="h-3.5 w-3.5" strokeWidth={2.1} aria-hidden="true" />);
};

const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const getRouteMarkerLabel = (item) => (isDraftRouteItem(item) ? getDraftRouteLabel(item) : (formatTimeSlot(item?.time_slot) || 'Маршрут'));
const getRouteMarkerClassName = (item) => `route-map-marker route-map-marker--${getRouteScheduleColor(item)}${item?.emergency_dispatch ? ' route-map-marker--emergency' : ''}`;
const getRouteMarkerAriaLabel = (item) => {
  if (isDraftRouteItem(item)) {
    return `${getDraftRouteLabel(item)} · ${item?.address_text || 'адрес создаваемого заказа'}`;
  }
  const routeNumber = item?.order_number ? ` ${item.order_number}` : '';
  return `${getRouteMarkerLabel(item)} · ${getRouteTitleLabel(item)}${routeNumber}`;
};

const getRouteMarkerCallIconSvg = () => (
  renderToStaticMarkup(<PhoneCall className="h-3 w-3" strokeWidth={2.25} aria-hidden="true" />)
);

const getRouteMarkerEmergencyIconSvg = () => (
  renderToStaticMarkup(<Siren className="h-3 w-3" strokeWidth={2.25} aria-hidden="true" />)
);

const hasRouteCallRequirement = (item) => {
  if (getRouteEntityType(item) !== 'order') return false;
  const requiredStage = String(item?.dispatcher_call_required_stage || '').trim();
  if (!requiredStage) return false;
  return requiredStage === String(item?.stage_name || '').trim();
};

const getRouteMarkerInnerHtml = (item) => {
  if (isDraftRouteItem(item)) {
    return `
      <span class="route-map-marker__draft-dot" aria-hidden="true">
        <span class="route-map-marker__draft-core"></span>
      </span>
    `;
  }

  const callIconHtml = hasRouteCallRequirement(item)
    ? `<span class="route-map-marker__call-icon" aria-hidden="true">${getRouteMarkerCallIconSvg()}</span>`
    : '';
  const emergencyIconHtml = item?.emergency_dispatch
    ? `<span class="route-map-marker__emergency-icon" aria-hidden="true">${getRouteMarkerEmergencyIconSvg()}</span>`
    : '';

  return `
    <span class="route-map-marker__time">
      <span class="route-map-marker__stage-badge route-map-marker__stage-badge--${getStageMeta(item).color || 'gray'}" aria-hidden="true">
        <span class="route-map-marker__stage-icon">${getRouteMarkerStageIconSvg(item.stage_name, getRouteEntityType(item))}</span>
      </span>
      <span class="route-map-marker__time-badge">${escapeHtml(getRouteMarkerLabel(item))}</span>
      ${callIconHtml}
      ${emergencyIconHtml}
    </span>
    <span class="route-map-marker__tail" aria-hidden="true"></span>
  `;
};

const getLegacyRouteMarkerLayoutHtml = (item) => (
  `<div class="${getRouteMarkerClassName(item)}">${getRouteMarkerInnerHtml(item)}</div>`
);

const getLegacyRouteClusterLayoutHtml = () => `
  <div class="route-map-cluster route-map-cluster--legacy">
    <span class="route-map-cluster__ring" aria-hidden="true"></span>
    <span class="route-map-cluster__count">$[properties.geoObjects.length]</span>
  </div>
`;

const spreadMarkers = (items) => {
  const groups = new Map();
  items.forEach((item) => {
    const key = `${Number(item.latitude).toFixed(6)}:${Number(item.longitude).toFixed(6)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });

  const result = [];
  groups.forEach((group) => {
    if (group.length === 1) {
      result.push({
        ...group[0],
        markerLat: Number(group[0].latitude),
        markerLng: Number(group[0].longitude)
      });
      return;
    }

    const radius = 0.00018;
    group.forEach((item, index) => {
      const angle = (Math.PI * 2 * index) / group.length;
      result.push({
        ...item,
        markerLat: Number(item.latitude) + Math.sin(angle) * radius,
        markerLng: Number(item.longitude) + Math.cos(angle) * radius
      });
    });
  });

  return result;
};

const getRouteEntityType = (item) => {
  if (isDraftRouteItem(item)) return 'draft_order';
  return item?.entity_type === 'task' ? 'task' : 'order';
};
const getRouteKey = (item) => (
  isDraftRouteItem(item)
    ? `draft_order:${item?.order_id || DRAFT_ORDER_ROUTE_ID}`
    : `${getRouteEntityType(item)}:${item?.order_id ?? ''}`
);
const getRouteClusterFeature = (item) => ({
  type: 'Feature',
  id: getRouteKey(item),
  geometry: {
    type: 'Point',
    coordinates: [item.markerLng, item.markerLat]
  },
  properties: {
    item
  }
});
const getClusterAnchorCoordinates = (coordinates, features = []) => {
  const fallback = Array.isArray(coordinates) ? coordinates : MAP_FALLBACK_CENTER;
  const sourceFeatures = Array.isArray(features) ? features : [];
  const [clusterLng, clusterLat] = fallback.map(Number);
  if (!Number.isFinite(clusterLng) || !Number.isFinite(clusterLat)) {
    return fallback;
  }

  let bestCoordinates = fallback;
  let bestDistance = Infinity;

  sourceFeatures.forEach((feature) => {
    const featureCoordinates = feature?.geometry?.coordinates;
    if (!Array.isArray(featureCoordinates) || featureCoordinates.length !== 2) return;
    const [lng, lat] = featureCoordinates.map(Number);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    const distance = ((lng - clusterLng) ** 2) + ((lat - clusterLat) ** 2);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestCoordinates = [lng, lat];
    }
  });

  return bestCoordinates;
};
const getOpenRouteHref = (item) => {
  if (isDraftRouteItem(item)) return '';
  return getRouteEntityType(item) === 'task'
    ? `#/tasks/${item.order_id}?from=routes`
    : `#/order/${item.order_id}?from=routes`;
};
const getRouteTitleLabel = (item) => {
  if (isDraftRouteItem(item)) return getDraftRouteLabel(item).toLowerCase();
  return getRouteEntityType(item) === 'task' ? 'поручение' : 'заказ';
};
const getRouteUnavailableLabel = (item) => (
  isDraftRouteItem(item)
    ? 'Этот заказ ещё не создан'
    : getRouteEntityType(item) === 'task'
    ? 'Это поручение не назначено на вас'
    : 'Этот заказ не назначен на вас'
);

const PRIVILEGED_TYPES = new Set(['admin', 'dispatcher', 'developer']);
const CALENDAR_WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const CALENDAR_MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

const RoutesMap = ({
  items = [],
  loading = false,
  hideRegionInAddress = false,
  currentUserId = null,
  currentUserType = 'user',
  onRegisterOpenDatePicker = null,
  focusRequest = null,
  routeContextBase = null,
  fixedDateKey = '',
  hideExternalNavigation = false,
  hideRouteDetailsLink = false,
  draftRouteItem = null,
  routeCardAsModal = false
}) => {
  const normalizedFixedDateKey = String(fixedDateKey || '').trim();
  const mapWrapperRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const currentLocationCoordsRef = useRef(null);
  const currentLocationMarkerRef = useRef(null);
  const mapEngineRef = useRef('v3');
  const forceLegacyRoutesMapRef = useRef(false);
  const markerRefs = useRef([]);
  const mapThemeRef = useRef(null);
  const hasInitializedDateRef = useRef(false);
  const mapLocationRef = useRef({
    center: null,
    zoom: MAP_DEFAULT_ZOOM,
    azimuth: 0,
    tilt: 0
  });
  const geocodeCacheRef = useRef(new Map());
  const persistedCoordsAttemptsRef = useRef(new Set());
  const suppressNextMapClickRef = useRef(false);
  const suppressMapClickUntilRef = useRef(0);
  const suppressNextMapClickTimerRef = useRef(null);
  const pendingClusterRouteOpenTimerRef = useRef(null);
  const [yandexApiKey, setYandexApiKey] = useState('');
  const [keyLoaded, setKeyLoaded] = useState(false);
  const [mapError, setMapError] = useState('');
  const [selectedDateKey, setSelectedDateKey] = useState(normalizedFixedDateKey);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [focusedRouteId, setFocusedRouteId] = useState(null);
  const [selectedClusterRouteIds, setSelectedClusterRouteIds] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [mapConfirmModal, setMapConfirmModal] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isGlobalDarkTheme, setIsGlobalDarkTheme] = useState(() => document.documentElement.classList.contains('dark'));
  const [isMapDarkMode, setIsMapDarkMode] = useState(() => document.documentElement.classList.contains('dark'));
  const [isMapTilted, setIsMapTilted] = useState(false);
  const [mapProvider, setMapProvider] = useState(getStoredMapProvider);
  const clusterGroupRef = useRef(null);
  const [isMap3DSupported, setIsMap3DSupported] = useState(true);
  const [resolvedCoordsByOrderId, setResolvedCoordsByOrderId] = useState({});
  const [isResolvingCoords, setIsResolvingCoords] = useState(false);
  const [isMapBootstrapping, setIsMapBootstrapping] = useState(false);
  const { toast, showToast, hideToast } = useToast(3200);
  useModalBackButton(!!selectedRouteId, () => setSelectedRouteId(null));
  useModalBackButton(!!selectedClusterRouteIds, () => setSelectedClusterRouteIds(null));
  useModalBackButton(showDatePicker, () => setShowDatePicker(false));
  useModalBackButton(!!mapConfirmModal, () => setMapConfirmModal(null));

  const syncCurrentLocationMarker = useCallback((coordinates = currentLocationCoordsRef.current) => {
    currentLocationMarkerRef.current = replaceCurrentLocationMarker({
      map: mapInstanceRef.current,
      engine: mapEngineRef.current,
      previousMarker: currentLocationMarkerRef.current,
      coordinates,
    });
  }, []);

  const destroyCurrentMap = useCallback(() => {
    if (mapInstanceRef.current) {
      try {
        if (typeof mapInstanceRef.current.remove === 'function') {
          mapInstanceRef.current.remove();
        } else if (typeof mapInstanceRef.current.destroy === 'function') {
          mapInstanceRef.current.destroy();
        }
      } catch (e) {
        console.warn('Ошибка при уничтожении карты:', e);
      }
      mapInstanceRef.current = null;
    }
    if (clusterGroupRef.current) {
      try {
        clusterGroupRef.current.clearLayers?.();
      } catch {}
      clusterGroupRef.current = null;
    }
    markerRefs.current = [];
    currentLocationMarkerRef.current = null;
    if (mapRef.current) {
      try {
        delete mapRef.current._leaflet_id;
      } catch {}
      mapRef.current.innerHTML = '';
      mapRef.current.className = mapRef.current.className
        .replace(/leaflet-[^\s]+/g, '')
        .trim();
    }
  }, []);

  const clearMapClickSuppression = useCallback(() => {
    suppressNextMapClickRef.current = false;
    suppressMapClickUntilRef.current = 0;
    if (suppressNextMapClickTimerRef.current) {
      window.clearTimeout(suppressNextMapClickTimerRef.current);
      suppressNextMapClickTimerRef.current = null;
    }
  }, []);

  const suppressNextMapClick = useCallback((duration = 1600) => {
    suppressNextMapClickRef.current = true;
    suppressMapClickUntilRef.current = Date.now() + duration;
    if (suppressNextMapClickTimerRef.current) {
      window.clearTimeout(suppressNextMapClickTimerRef.current);
    }
    suppressNextMapClickTimerRef.current = window.setTimeout(() => {
      clearMapClickSuppression();
    }, duration);
  }, [clearMapClickSuppression]);

  const shouldIgnoreMapClick = useCallback(() => {
    if (!suppressNextMapClickRef.current && !suppressMapClickUntilRef.current) {
      return false;
    }
    if (Date.now() <= suppressMapClickUntilRef.current) {
      return true;
    }
    clearMapClickSuppression();
    return false;
  }, [clearMapClickSuppression]);

  const deferClusterRouteOpen = useCallback((routeKey) => {
    if (pendingClusterRouteOpenTimerRef.current) {
      window.clearTimeout(pendingClusterRouteOpenTimerRef.current);
    }
    pendingClusterRouteOpenTimerRef.current = window.setTimeout(() => {
      pendingClusterRouteOpenTimerRef.current = null;
      setSelectedRouteId(routeKey);
    }, 0);
  }, []);

  const stopClusterSelectionEvent = useCallback((event, preventDefault = false) => {
    if (!event) return;
    if (preventDefault) {
      event.preventDefault?.();
    }
    event.stopPropagation?.();
    event.nativeEvent?.stopImmediatePropagation?.();
    event.nativeEvent?.stopPropagation?.();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadKey = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings/yandex_maps_api_key`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Не удалось загрузить ключ Яндекс.Карт');
        }

        const data = await response.json();
        if (!cancelled) {
          setYandexApiKey(data.value || '');
          setKeyLoaded(true);
        }
      } catch (error) {
        console.error('Ошибка загрузки ключа Яндекс.Карт:', error);
        if (!cancelled) {
          setMapError('Не удалось загрузить настройки карты');
          setKeyLoaded(true);
        }
      }
    };

    loadKey();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    forceLegacyRoutesMapRef.current = false;
    mapEngineRef.current = 'v3';
    setIsMap3DSupported(true);
  }, [yandexApiKey]);

  useEffect(() => () => {
    if (suppressNextMapClickTimerRef.current) {
      window.clearTimeout(suppressNextMapClickTimerRef.current);
    }
    if (pendingClusterRouteOpenTimerRef.current) {
      window.clearTimeout(pendingClusterRouteOpenTimerRef.current);
    }
  }, []);

  const moveMapToPoint = (center, duration = 180, zoom = null) => {
    if (!mapInstanceRef.current) return;
    const hasExplicitZoom = zoom !== null && zoom !== undefined && zoom !== '';
    const targetZoom = hasExplicitZoom
      ? getSafeMapZoom(zoom)
      : getSafeMapZoom(mapLocationRef.current.zoom);
    if (mapEngineRef.current === 'leaflet') {
      mapInstanceRef.current.setView([center[1], center[0]], targetZoom, {
        animate: true,
        duration: duration / 1000
      });
      mapLocationRef.current = {
        ...mapLocationRef.current,
        center,
        zoom: targetZoom
      };
      return;
    }

    if (mapEngineRef.current === 'v3') {
      mapInstanceRef.current.setLocation({
        center,
        zoom: targetZoom,
        duration
      });
      mapLocationRef.current = {
        ...mapLocationRef.current,
        center,
        zoom: targetZoom
      };
      return;
    }

    mapInstanceRef.current.setCenter?.(
      [center[1], center[0]],
      targetZoom,
      { duration }
    );
    mapLocationRef.current = {
      ...mapLocationRef.current,
      center,
      zoom: targetZoom
    };
  };

  const syncCurrentMapViewport = () => {
    if (!mapInstanceRef.current) return;
    if (mapEngineRef.current === 'leaflet') {
      try {
        if (mapRef.current && !mapRef.current.classList.contains('leaflet-container')) {
          mapRef.current.classList.add(
            'leaflet-container',
            'leaflet-touch',
            'leaflet-retina',
            'leaflet-fade-anim',
            'leaflet-grab',
            'leaflet-touch-drag',
            'leaflet-touch-zoom'
          );
        }
        mapInstanceRef.current.invalidateSize?.({ animate: false });
      } catch (err) {
        console.warn('Ошибка syncCurrentMapViewport (Leaflet):', err);
      }
      return;
    }
    if (!mapLocationRef.current.center) return;

    if (mapEngineRef.current === 'v3') {
      mapInstanceRef.current.update({
        location: {
          center: mapLocationRef.current.center,
          zoom: mapLocationRef.current.zoom
        },
        camera: {
          azimuth: mapLocationRef.current.azimuth,
          tilt: mapLocationRef.current.tilt
        }
      });
      return;
    }

    mapInstanceRef.current.container?.fitToViewport?.();
    mapInstanceRef.current.setCenter?.(
      [mapLocationRef.current.center[1], mapLocationRef.current.center[0]],
      mapLocationRef.current.zoom || MAP_DEFAULT_ZOOM,
      { duration: 0 }
    );
  };

  const normalizedDraftRouteItem = useMemo(() => {
    const addressText = String(draftRouteItem?.address_text || '').trim();
    const dateKey = String(draftRouteItem?.scheduled_date_key || normalizedFixedDateKey || selectedDateKey || '').trim();
    if (!addressText || !dateKey) return null;

    return {
      ...draftRouteItem,
      entity_type: 'draft_order',
      is_draft_order: true,
      order_id: draftRouteItem?.order_id || DRAFT_ORDER_ROUTE_ID,
      order_number: draftRouteItem?.order_number || 'Новый',
      stage_name: 'draft_order',
      scheduled_date_key: dateKey,
      scheduled_date: draftRouteItem?.scheduled_date || `${dateKey}T00:00:00`,
      time_slot: '',
      address_text: addressText,
      lifecycle_status: 'draft',
      lifecycle_label: 'Новый заказ'
    };
  }, [draftRouteItem, normalizedFixedDateKey, selectedDateKey]);

  const sourceItems = useMemo(() => (
    normalizedDraftRouteItem ? [...items, normalizedDraftRouteItem] : items
  ), [items, normalizedDraftRouteItem]);

  const dateGroups = useMemo(() => {
    const groups = new Map();
    sourceItems.forEach((item) => {
      const key = item.scheduled_date_key || '';
      if (!key) return;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });

    return Array.from(groups.entries()).map(([dateKey, list]) => ({
      dateKey,
      count: list.length
    }));
  }, [sourceItems]);

  const dateCounts = useMemo(() => {
    const next = new Map();
    dateGroups.forEach((group) => {
      next.set(group.dateKey, group.count);
    });
    return next;
  }, [dateGroups]);

  useEffect(() => {
    if (!selectedDateKey) return;
    const selectedDate = new Date(`${selectedDateKey}T00:00:00`);
    if (!Number.isNaN(selectedDate.getTime())) {
      setCalendarMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    }
  }, [selectedDateKey]);

  useEffect(() => {
    if (!normalizedFixedDateKey) return;
    setSelectedDateKey(normalizedFixedDateKey);
    setSelectedRouteId(null);
    setFocusedRouteId(null);
    setSelectedClusterRouteIds(null);
    setShowDatePicker(false);
    hasInitializedDateRef.current = true;
  }, [normalizedFixedDateKey]);

  useEffect(() => {
    if (normalizedFixedDateKey) {
      if (selectedDateKey !== normalizedFixedDateKey) {
        setSelectedDateKey(normalizedFixedDateKey);
      }
      hasInitializedDateRef.current = true;
      return;
    }

    if (!dateGroups.length) {
      setSelectedDateKey('');
      return;
    }

    if (!selectedDateKey || !dateGroups.some((group) => group.dateKey === selectedDateKey)) {
      const todayKey = formatCalendarDateKey(new Date());
      const nextDateKey = dateGroups.find((group) => group.dateKey === todayKey)?.dateKey || dateGroups[0]?.dateKey || '';
      const shouldExplainSwitch = hasInitializedDateRef.current && selectedDateKey && nextDateKey && selectedDateKey !== nextDateKey;
      setSelectedDateKey(nextDateKey);
      if (shouldExplainSwitch) {
        showToast(
          `На ${formatDateChip(selectedDateKey)} маршрутов нет, показываем ${formatDateChip(nextDateKey)}.`,
          'info'
        );
      }
      hasInitializedDateRef.current = true;
      return;
    }

    hasInitializedDateRef.current = true;
  }, [dateGroups, normalizedFixedDateKey, selectedDateKey, showToast]);

  const visibleItems = useMemo(() => {
    if (!selectedDateKey) return [];
    return sourceItems.filter((item) => item.scheduled_date_key === selectedDateKey);
  }, [sourceItems, selectedDateKey]);

  const visibleItemsWithResolvedCoords = useMemo(() => visibleItems.map((item) => {
    if (hasValidCoords(item)) {
      return {
        ...item,
        isDerivedCoords: false
      };
    }

    const resolved = resolvedCoordsByOrderId[getRouteKey(item)];
    if (resolved?.latitude !== undefined && resolved?.longitude !== undefined) {
      return {
        ...item,
        latitude: resolved.latitude,
        longitude: resolved.longitude,
        isDerivedCoords: true
      };
    }

    return {
      ...item,
      isDerivedCoords: false
    };
  }), [visibleItems, resolvedCoordsByOrderId]);

  const geocodedItems = useMemo(() => visibleItemsWithResolvedCoords.filter(hasValidCoords), [visibleItemsWithResolvedCoords]);
  const skippedItemsCount = visibleItems.length - geocodedItems.length;
  const spreadItems = useMemo(() => spreadMarkers(geocodedItems), [geocodedItems]);

  useEffect(() => {
    if (!visibleItems.length) return undefined;

    const unresolvedItems = visibleItems.filter((item) => {
      if (hasValidCoords(item)) return false;
      if (!String(item.address_text || '').trim()) return false;
      if (resolvedCoordsByOrderId[getRouteKey(item)]) return false;
      return true;
    });

    if (!unresolvedItems.length) return undefined;

    let cancelled = false;

    const resolveCoords = async () => {
      setIsResolvingCoords(true);
      try {
        let ymaps = null;
        if (yandexApiKey) {
          try {
            ymaps = await loadYandexMaps(yandexApiKey);
          } catch {
            ymaps = null;
          }
        }
        if (cancelled) return;

        const resolvedBatch = {};

        for (const item of unresolvedItems) {
          const addressKey = String(item.address_text || '').trim().toLowerCase();
          if (!addressKey) continue;

          if (geocodeCacheRef.current.has(addressKey)) {
            const cached = geocodeCacheRef.current.get(addressKey);
            if (cached) {
               resolvedBatch[getRouteKey(item)] = cached;
            }
            continue;
          }

          let resolved = null;

          // 1. Пробуем Яндекс Геокодер
          if (ymaps) {
            try {
              const result = await ymaps.geocode(item.address_text, { results: 1 });
              const firstGeoObject = result?.geoObjects?.get?.(0);
              const coords = firstGeoObject?.geometry?.getCoordinates?.();
              if (Array.isArray(coords) && coords.length === 2 && coords.every((value) => Number.isFinite(Number(value)))) {
                resolved = {
                  latitude: Number(coords[0]),
                  longitude: Number(coords[1])
                };
              }
            } catch {
              resolved = null;
            }
          }

          // 2. Резервный геокодер OpenStreetMap (Nominatim)
          if (!resolved) {
            try {
              const osmCoords = await geocodeAddressWithNominatim(item.address_text);
              if (osmCoords) {
                resolved = {
                  latitude: osmCoords.lat,
                  longitude: osmCoords.lng
                };
              }
            } catch {
              resolved = null;
            }
          }

          if (resolved) {
            geocodeCacheRef.current.set(addressKey, resolved);
            resolvedBatch[getRouteKey(item)] = resolved;
          } else {
            geocodeCacheRef.current.set(addressKey, null);
          }
        }

        if (!cancelled && Object.keys(resolvedBatch).length > 0) {
          setResolvedCoordsByOrderId((prev) => ({
            ...prev,
            ...resolvedBatch
          }));
        }
      } finally {
        if (!cancelled) {
          setIsResolvingCoords(false);
        }
      }
    };

    resolveCoords();

    return () => {
      cancelled = true;
    };
  }, [resolvedCoordsByOrderId, visibleItems, yandexApiKey]);

  useEffect(() => {
    if (selectedRouteId !== null && !spreadItems.some((item) => getRouteKey(item) === selectedRouteId)) {
      setSelectedRouteId(null);
    }
  }, [selectedRouteId, spreadItems]);

  useEffect(() => {
    if (focusedRouteId !== null && !spreadItems.some((item) => getRouteKey(item) === focusedRouteId)) {
      setFocusedRouteId(null);
    }
  }, [focusedRouteId, spreadItems]);

  useEffect(() => {
    if (!selectedClusterRouteIds?.length) return;
    if (!selectedClusterRouteIds.some((routeId) => spreadItems.some((item) => getRouteKey(item) === routeId))) {
      setSelectedClusterRouteIds(null);
    }
  }, [selectedClusterRouteIds, spreadItems]);

  useEffect(() => {
    if (!focusRequest?.orderId && !focusRequest?.routeKey) return;

    if (focusRequest.scheduledDateKey && !normalizedFixedDateKey) {
      setSelectedDateKey(focusRequest.scheduledDateKey);
    }
    setSelectedRouteId(null);
    setFocusedRouteId(null);
    setSelectedClusterRouteIds(null);
  }, [focusRequest, normalizedFixedDateKey]);

  useEffect(() => {
    if ((!focusRequest?.orderId && !focusRequest?.routeKey) || !mapInstanceRef.current) return;
    const targetRoute = spreadItems.find((item) => (
      focusRequest.routeKey
        ? getRouteKey(item) === focusRequest.routeKey
        : (item.order_id === focusRequest.orderId && getRouteEntityType(item) === (focusRequest.entityType || 'order'))
    ));
    if (!targetRoute) return;

    moveMapToPoint([targetRoute.markerLng, targetRoute.markerLat], 180, MAP_ROUTE_FOCUS_ZOOM);
    setFocusedRouteId(getRouteKey(targetRoute));
    setSelectedRouteId(null);
  }, [focusRequest, isMapBootstrapping, spreadItems]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    const itemsToPersist = visibleItems.filter((item) => {
      if (isDraftRouteItem(item)) return false;
      if (hasValidCoords(item)) return false;
      const resolved = resolvedCoordsByOrderId[getRouteKey(item)];
      if (resolved?.latitude === undefined || resolved?.longitude === undefined) return false;
      const stagePrefix = item.stage_name === 'delivery' ? 'delivery' : 'pickup';
      const persistKey = `${getRouteKey(item)}:${stagePrefix}:${resolved.latitude}:${resolved.longitude}`;
      if (persistedCoordsAttemptsRef.current.has(persistKey)) return false;
      persistedCoordsAttemptsRef.current.add(persistKey);
      return true;
    });

    if (!itemsToPersist.length) return undefined;

    let cancelled = false;

    const persistResolvedCoords = async () => {
      await Promise.allSettled(itemsToPersist.map(async (item) => {
        const resolved = resolvedCoordsByOrderId[getRouteKey(item)];
        if (!resolved) return;

        const isTask = getRouteEntityType(item) === 'task';
        const payload = {
          ...(isTask ? {} : { stage_name: item.stage_name === 'delivery' ? 'delivery' : 'pickup' }),
          latitude: resolved.latitude,
          longitude: resolved.longitude
        };

        try {
          const response = await fetch(
            isTask
              ? `${API_URL}/api/tasks/${item.order_id}/route-coordinates`
              : `${API_URL}/api/orders/${item.order_id}/route-coordinates`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
        } catch (error) {
          if (!cancelled) {
            console.error('Не удалось сохранить координаты маршрута:', error);
          }
        }
      }));
    };

    persistResolvedCoords();

    return () => {
      cancelled = true;
    };
  }, [resolvedCoordsByOrderId, visibleItems]);

  const selectedRoute = useMemo(
    () => spreadItems.find((item) => getRouteKey(item) === selectedRouteId) || null,
    [selectedRouteId, spreadItems]
  );

  const selectedClusterRoutes = useMemo(() => {
    if (!selectedClusterRouteIds?.length) return [];
    const routeById = new Map(spreadItems.map((item) => [getRouteKey(item), item]));
    return selectedClusterRouteIds.map((routeId) => routeById.get(routeId)).filter(Boolean);
  }, [selectedClusterRouteIds, spreadItems]);

  useEffect(() => {
    if (!mapRef.current) return undefined;
    if (mapProvider === 'yandex' && !yandexApiKey) return undefined;

    let cancelled = false;
    let stopSuppressingOpenMapsButton = null;

    const createRouteMarkerElement = (item) => {
      const markerElement = document.createElement('button');
      markerElement.type = 'button';
      markerElement.className = getRouteMarkerClassName(item);
      markerElement.setAttribute('aria-label', getRouteMarkerAriaLabel(item));
      markerElement.innerHTML = getRouteMarkerInnerHtml(item);

      markerElement.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!mapInstanceRef.current) return;
        haptic('tap');
        setSelectedClusterRouteIds(null);
        const routeKey = getRouteKey(item);
        setFocusedRouteId(routeKey);
        setSelectedRouteId(routeKey);
        moveMapToPoint([item.markerLng, item.markerLat], 180, MAP_ROUTE_FOCUS_ZOOM);
      });

      return markerElement;
    };

    const syncLeafletMap = () => {
      try {
        const isFirstMount = !mapInstanceRef.current;
        if (isFirstMount) {
          setIsMapBootstrapping(true);
        }

        if (mapInstanceRef.current && mapEngineRef.current !== 'leaflet') {
          destroyCurrentMap();
        }

        const previousCenter = mapLocationRef.current.center;
        const previousZoom = mapLocationRef.current.zoom ?? null;
        const savedCenter = previousCenter || getSavedMapCenter();
        const savedZoom = previousZoom ?? getSavedMapZoom();
        const firstPoint = spreadItems[0] ? [spreadItems[0].markerLng, spreadItems[0].markerLat] : null;
        const initialCenter = savedCenter || firstPoint || MAP_FALLBACK_CENTER;
        const selectedMapRoute = selectedRouteId
          ? spreadItems.find((item) => getRouteKey(item) === selectedRouteId)
          : null;
        const selectedMapRouteKey = selectedMapRoute ? getRouteKey(selectedMapRoute) : null;
        const clusterableSpreadItems = selectedMapRouteKey
          ? spreadItems.filter((item) => getRouteKey(item) !== selectedMapRouteKey)
          : spreadItems;

        let map = mapInstanceRef.current;

        const shouldRecreateMap = !!(
          map && mapThemeRef.current !== isMapDarkMode
        );

        if (shouldRecreateMap && map) {
          try {
            map.remove();
          } catch {}
          map = null;
          mapInstanceRef.current = null;
        }

        if (!map) {
          if (mapRef.current) {
            try {
              delete mapRef.current._leaflet_id;
            } catch {}
            mapRef.current.innerHTML = '';
            mapRef.current.className = mapRef.current.className
              .replace(/leaflet-[^\s]+/g, '')
              .trim();
          }
          markerRefs.current = [];
          map = L.map(mapRef.current, {
          center: [initialCenter[1], initialCenter[0]],
          zoom: savedZoom ?? MAP_DEFAULT_ZOOM,
          zoomControl: false,
          attributionControl: false
        });

        const tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

        L.tileLayer(tileUrl, {
          maxZoom: 19,
          subdomains: ['a', 'b', 'c']
        }).addTo(map);

        mapLocationRef.current = {
          center: [initialCenter[0], initialCenter[1]],
          zoom: savedZoom ?? MAP_DEFAULT_ZOOM,
          azimuth: 0,
          tilt: 0
        };

        map.on('moveend zoomend', () => {
          const c = map.getCenter();
          const z = map.getZoom();
          mapLocationRef.current = {
            center: [c.lng, c.lat],
            zoom: z,
            azimuth: 0,
            tilt: 0
          };
          saveMapCenter([c.lng, c.lat]);
          saveMapZoom(z);
        });

        map.on('click', () => {
          if (shouldIgnoreMapClick()) {
            return;
          }
          setSelectedRouteId(null);
          setSelectedClusterRouteIds(null);
        });

        mapEngineRef.current = 'leaflet';
        mapInstanceRef.current = map;
        mapThemeRef.current = isMapDarkMode;
        setTimeout(() => {
          if (!cancelled && mapInstanceRef.current && mapEngineRef.current === 'leaflet') {
            mapInstanceRef.current.invalidateSize?.({ animate: false });
          }
        }, 50);
      }

      if (clusterGroupRef.current) {
        try {
          map.removeLayer(clusterGroupRef.current);
        } catch {}
        clusterGroupRef.current = null;
      }

      markerRefs.current.forEach((marker) => {
        try {
          map.removeLayer(marker);
        } catch {}
      });
      markerRefs.current = [];

      const clusterGroup = L.markerClusterGroup({
        showCoverageOnHover: false,
        zoomToBoundsOnClick: false,
        spiderfyOnMaxZoom: false,
        maxClusterRadius: 50,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount();
          return L.divIcon({
            className: 'leaflet-div-icon',
            html: `
              <div class="route-map-cluster">
                <span class="route-map-cluster__ring" aria-hidden="true"></span>
                <span class="route-map-cluster__count">${count}</span>
              </div>
            `,
            iconSize: [0, 0],
            iconAnchor: [0, 0]
          });
        }
      });

      const handleMarkerClick = (item) => {
        if (!mapInstanceRef.current) return;
        haptic('tap');
        setSelectedClusterRouteIds(null);
        const routeKey = getRouteKey(item);
        setFocusedRouteId(routeKey);
        setSelectedRouteId(routeKey);
        moveMapToPoint([item.markerLng, item.markerLat], 180, MAP_ROUTE_FOCUS_ZOOM);
      };

      clusterableSpreadItems.forEach((item) => {
        const marker = L.marker([item.markerLat, item.markerLng], {
          icon: L.divIcon({
            className: 'leaflet-div-icon',
            html: `<button type="button" class="${getRouteMarkerClassName(item)}" aria-label="${escapeHtml(getRouteMarkerAriaLabel(item))}">${getRouteMarkerInnerHtml(item)}</button>`,
            iconSize: [0, 0],
            iconAnchor: [0, 0]
          })
        });
        marker.options.item = item;
        marker.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          handleMarkerClick(item);
        });
        clusterGroup.addLayer(marker);
      });

      clusterGroup.on('clusterclick', (a) => {
        const childMarkers = a.layer.getAllChildMarkers();
        const items = childMarkers.map((m) => m.options.item).filter(Boolean);
        if (items.length) {
          haptic('open');
          setSelectedRouteId(null);
          setSelectedClusterRouteIds(items.map(getRouteKey));
        }
      });

      map.addLayer(clusterGroup);
      clusterGroupRef.current = clusterGroup;

      if (selectedMapRoute) {
        const focusedMarker = L.marker([selectedMapRoute.markerLat, selectedMapRoute.markerLng], {
          icon: L.divIcon({
            className: 'leaflet-div-icon',
            html: `<button type="button" class="${getRouteMarkerClassName(selectedMapRoute)}" aria-label="${escapeHtml(getRouteMarkerAriaLabel(selectedMapRoute))}">${getRouteMarkerInnerHtml(selectedMapRoute)}</button>`,
            iconSize: [0, 0],
            iconAnchor: [0, 0]
          }),
          zIndexOffset: 3000
        }).addTo(map);
        focusedMarker.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          handleMarkerClick(selectedMapRoute);
        });
        markerRefs.current.push(focusedMarker);
      }

        syncCurrentLocationMarker();
        setIsMap3DSupported(false);
        setIsMapTilted(false);
        setMapError('');
        setIsMapBootstrapping(false);
      } catch (leafletError) {
        console.error('Ошибка инициализации карты маршрутов (Leaflet):', leafletError);
        if (!cancelled) {
          setIsMapBootstrapping(false);
          setMapError('Не удалось инициализировать карту OpenStreetMap');
        }
      }
    };

    const syncMap = async () => {
      const isFirstMount = !mapInstanceRef.current;
      if (isFirstMount) {
        setIsMapBootstrapping(true);
      }

      if (mapInstanceRef.current && mapEngineRef.current === 'leaflet') {
        destroyCurrentMap();
      }

      try {
        const previousCenter = mapLocationRef.current.center;
        const previousZoom = mapLocationRef.current.zoom ?? null;
        const savedCenter = previousCenter || getSavedMapCenter();
        const savedZoom = previousZoom ?? getSavedMapZoom();
        const firstPoint = spreadItems[0] ? [spreadItems[0].markerLng, spreadItems[0].markerLat] : null;
        const initialCenter = savedCenter || firstPoint || MAP_FALLBACK_CENTER;
        const selectedMapRoute = selectedRouteId
          ? spreadItems.find((item) => getRouteKey(item) === selectedRouteId)
          : null;
        const selectedMapRouteKey = selectedMapRoute ? getRouteKey(selectedMapRoute) : null;
        const clusterableSpreadItems = selectedMapRouteKey
          ? spreadItems.filter((item) => getRouteKey(item) !== selectedMapRouteKey)
          : spreadItems;
        if (!forceLegacyRoutesMapRef.current) {
          try {
            const { ymaps3 } = await loadYandexMapsV3(yandexApiKey);
            const { YMapClusterer, clusterByGrid } = await ymaps3.import('@yandex/ymaps3-clusterer');
            if (cancelled || !mapRef.current) return;

            const shouldRecreateMap = !!(
              mapInstanceRef.current
              && mapThemeRef.current !== isMapDarkMode
            );

            if (shouldRecreateMap && mapInstanceRef.current) {
              markerRefs.current = [];
              mapInstanceRef.current.destroy?.();
              mapInstanceRef.current = null;
            }

            let map = mapInstanceRef.current;

            if (!map) {
              if (mapRef.current) {
                mapRef.current.innerHTML = '';
                mapRef.current.className = mapRef.current.className
                  .replace(/leaflet-[^\s]+/g, '')
                  .trim();
              }
              markerRefs.current = [];
              map = new ymaps3.YMap(mapRef.current, {
                location: {
                  center: initialCenter,
                  zoom: savedZoom ?? MAP_DEFAULT_ZOOM
                },
                copyrightsPosition: 'bottom right',
                distribution: false,
                distributionPosition: 'bottom right',
                mode: 'vector',
                behaviors: ['drag', 'scrollZoom', 'pinchZoom', 'pinchRotate', 'dblClick', 'magnifier', 'mouseTilt']
              });

              map.addChild(new ymaps3.YMapDefaultSchemeLayer({
                theme: isMapDarkMode ? 'dark' : 'light'
              }));
              map.addChild(new ymaps3.YMapDefaultFeaturesLayer({
                zIndex: 1800
              }));

              const listener = new ymaps3.YMapListener({
                layer: 'any',
                onClick: () => {
                  if (shouldIgnoreMapClick()) {
                    return;
                  }
                  setSelectedRouteId(null);
                  setSelectedClusterRouteIds(null);
                },
                onUpdate: ({ location, camera }) => {
                  if (location?.center) {
                    mapLocationRef.current = {
                      center: location.center,
                      zoom: Number.isFinite(location.zoom) ? location.zoom : mapLocationRef.current.zoom,
                      azimuth: Number.isFinite(camera?.azimuth) ? camera.azimuth : mapLocationRef.current.azimuth,
                      tilt: Number.isFinite(camera?.tilt) ? camera.tilt : mapLocationRef.current.tilt
                    };
                    setIsMapTilted((camera?.tilt || 0) > 0.05);
                    try {
                      localStorage.setItem(ROUTES_MAP_CENTER_KEY, JSON.stringify(location.center));
                      localStorage.setItem(ROUTES_MAP_ZOOM_KEY, String(mapLocationRef.current.zoom));
                    } catch {
                      // no-op
                    }
                  }
                }
              });
              map.addChild(listener);

              mapLocationRef.current = {
                center: initialCenter,
                zoom: savedZoom ?? MAP_DEFAULT_ZOOM,
                azimuth: 0,
                tilt: 0
              };

              mapInstanceRef.current = map;
              mapThemeRef.current = isMapDarkMode;
            }

            markerRefs.current.forEach((marker) => {
              try {
                map.removeChild(marker);
              } catch {
                // no-op
              }
            });
            markerRefs.current = [];

            const createRouteMarkerElement = (item) => {
              const markerElement = document.createElement('button');
              markerElement.type = 'button';
              markerElement.className = getRouteMarkerClassName(item);
              markerElement.setAttribute('aria-label', getRouteMarkerAriaLabel(item));
              markerElement.innerHTML = getRouteMarkerInnerHtml(item);

              markerElement.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                if (!mapInstanceRef.current) return;
                haptic('tap');
                setSelectedClusterRouteIds(null);
                const routeKey = getRouteKey(item);
                setFocusedRouteId(routeKey);
                setSelectedRouteId(routeKey);
                moveMapToPoint([item.markerLng, item.markerLat], 180, MAP_ROUTE_FOCUS_ZOOM);
              });

              return markerElement;
            };

            const createYMapRouteMarker = (item) => new ymaps3.YMapMarker({
              coordinates: [item.markerLng, item.markerLat]
            }, createRouteMarkerElement(item));

            const routeFeatures = clusterableSpreadItems.map(getRouteClusterFeature);
            if (routeFeatures.length) {
              const marker = (feature) => {
                const item = feature.properties?.item;
                return createYMapRouteMarker(item);
              };

              const cluster = (coordinates, features) => {
                const clusterItems = features
                  .map((feature) => feature.properties?.item)
                  .filter(Boolean);
                const clusterAnchorCoordinates = getClusterAnchorCoordinates(coordinates, features);
                const clusterElement = document.createElement('button');
                clusterElement.type = 'button';
                clusterElement.className = 'route-map-cluster';
                clusterElement.setAttribute('aria-label', `Выбрать одну из ${clusterItems.length} точек маршрута`);
                clusterElement.innerHTML = `
                  <span class="route-map-cluster__ring" aria-hidden="true"></span>
                  <span class="route-map-cluster__count">${clusterItems.length}</span>
                `;
                clusterElement.addEventListener('click', (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!clusterItems.length) return;
                  haptic('open');
                  setSelectedRouteId(null);
                  setSelectedClusterRouteIds(clusterItems.map(getRouteKey));
                });

                return new ymaps3.YMapMarker({
                  coordinates: clusterAnchorCoordinates
                }, clusterElement);
              };

              const clusterer = new YMapClusterer({
                method: clusterByGrid({ gridSize: 64 }),
                features: routeFeatures,
                marker,
                cluster
              });

              markerRefs.current.push(clusterer);
              map.addChild(clusterer);
            }

            if (selectedMapRoute) {
              const selectedMarker = createYMapRouteMarker(selectedMapRoute);
              markerRefs.current.push(selectedMarker);
              map.addChild(selectedMarker);
            }

            if (!spreadItems.length && mapInstanceRef.current && previousCenter) {
              mapInstanceRef.current.update({
                location: {
                  center: previousCenter,
                  zoom: previousZoom ?? MAP_DEFAULT_ZOOM
                }
              });
            }

            mapEngineRef.current = 'v3';
            syncCurrentLocationMarker();
            stopSuppressingOpenMapsButton?.();
            stopSuppressingOpenMapsButton = suppressYandexOpenMapsButton(mapRef.current);
            setIsMap3DSupported(true);
            setMapError('');
          } catch (v3Error) {
            forceLegacyRoutesMapRef.current = true;
            console.warn('Яндекс.Карты v3 недоступны, используем fallback на v2:', v3Error);
          }
        }

        if (forceLegacyRoutesMapRef.current) {
          const ymaps = await loadYandexMaps(yandexApiKey);
          if (cancelled || !mapRef.current) return;

          if (mapInstanceRef.current && mapEngineRef.current !== 'v2') {
            destroyCurrentMap();
          }

          const legacyInitialCenter = [initialCenter[1], initialCenter[0]];
          let map = mapInstanceRef.current;

          if (!map) {
            if (mapRef.current) {
              mapRef.current.innerHTML = '';
              mapRef.current.className = mapRef.current.className
                .replace(/leaflet-[^\s]+/g, '')
                .trim();
            }
            markerRefs.current = [];
            map = new ymaps.Map(mapRef.current, {
              center: legacyInitialCenter,
              zoom: savedZoom ?? MAP_DEFAULT_ZOOM,
              controls: []
            }, {
              suppressMapOpenBlock: true
            });

            map.events.add('click', (event) => {
              if (event?.get?.('target') === map) {
                if (shouldIgnoreMapClick()) {
                  return;
                }
                setSelectedRouteId(null);
              }
            });
            map.events.add('boundschange', () => {
              const center = map.getCenter?.();
              const zoom = map.getZoom?.();
              if (!center) return;
              const normalizedCenter = [Number(center[1]), Number(center[0])];
              mapLocationRef.current = {
                center: normalizedCenter,
                zoom: Number.isFinite(zoom) ? zoom : mapLocationRef.current.zoom,
                azimuth: 0,
                tilt: 0
              };
              setIsMapTilted(false);
              try {
                localStorage.setItem(ROUTES_MAP_CENTER_KEY, JSON.stringify(normalizedCenter));
                localStorage.setItem(ROUTES_MAP_ZOOM_KEY, String(mapLocationRef.current.zoom));
              } catch {
                // no-op
              }
            });

            mapLocationRef.current = {
              center: initialCenter,
              zoom: savedZoom ?? MAP_DEFAULT_ZOOM,
              azimuth: 0,
              tilt: 0
            };
            mapInstanceRef.current = map;
          } else {
            markerRefs.current.forEach((geoObject) => {
              try {
                map.geoObjects.remove(geoObject);
              } catch {
                // no-op
              }
            });
            markerRefs.current = [];
          }

          const createLegacyRoutePlacemark = (item) => {
            const markerLayout = ymaps.templateLayoutFactory.createClass(
              getLegacyRouteMarkerLayoutHtml(item)
            );
            const marker = new ymaps.Placemark([item.markerLat, item.markerLng], {
              hintContent: getRouteMarkerAriaLabel(item),
              routeKey: getRouteKey(item)
            }, {
              iconLayout: markerLayout,
              iconShape: LEGACY_ROUTE_MARKER_ICON_SHAPE,
              cursor: 'pointer'
            });
            marker.events.add('click', () => {
              haptic('tap');
              setSelectedClusterRouteIds(null);
              const routeKey = getRouteKey(item);
              setFocusedRouteId(routeKey);
              setSelectedRouteId(routeKey);
              moveMapToPoint([item.markerLng, item.markerLat], 180, MAP_ROUTE_FOCUS_ZOOM);
            });
            return marker;
          };

          const legacyClusterLayout = ymaps.templateLayoutFactory.createClass(
            getLegacyRouteClusterLayoutHtml()
          );
          const legacyClusterer = new ymaps.Clusterer({
            clusterDisableClickZoom: true,
            clusterOpenBalloonOnClick: false,
            clusterIconLayout: legacyClusterLayout,
            clusterIconShape: {
              type: 'Circle',
              coordinates: [0, 0],
              radius: 30
            },
            gridSize: 64,
            minClusterSize: 2
          });

          legacyClusterer.events.add('click', (event) => {
            const target = event?.get?.('target');
            if (!target || typeof target.getGeoObjects !== 'function') return;

            const routeIds = target.getGeoObjects()
              .map((geoObject) => geoObject?.properties?.get?.('routeKey'))
              .filter(Boolean);
            if (!routeIds.length) return;

            haptic('open');
            setSelectedRouteId(null);
            setSelectedClusterRouteIds(routeIds);
          });

          const legacyMarkers = clusterableSpreadItems.map(createLegacyRoutePlacemark);
          if (legacyMarkers.length) {
            legacyClusterer.add(legacyMarkers);
            map.geoObjects.add(legacyClusterer);
            markerRefs.current.push(legacyClusterer);
          }

          if (selectedMapRoute) {
            const selectedLegacyMarker = createLegacyRoutePlacemark(selectedMapRoute);
            map.geoObjects.add(selectedLegacyMarker);
            markerRefs.current.push(selectedLegacyMarker);
          }

          mapInstanceRef.current = map;
          mapThemeRef.current = isMapDarkMode;
          mapEngineRef.current = 'v2';
          syncCurrentLocationMarker();
          setIsMap3DSupported(false);
          setIsMapTilted(false);
          setMapError('');
        }

        if (isFirstMount && !cancelled) {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              if (!cancelled) {
                setIsMapBootstrapping(false);
              }
            });
          });
        }
      } catch (error) {
        console.error('Ошибка инициализации карты маршрутов (Яндекс):', error);
        if (!cancelled) {
          setIsMapBootstrapping(false);
          if (!window.ymaps3 && !window.ymaps) {
            setMapProvider('osm');
            showToast('Яндекс.Карты недоступны, переключаем на OpenStreetMap', 'warning');
          }
        }
      }
    };

    if (mapProvider === 'osm') {
      syncLeafletMap();
    } else {
      syncMap();
    }

    return () => {
      cancelled = true;
      stopSuppressingOpenMapsButton?.();
      if (clusterGroupRef.current) {
        try {
          clusterGroupRef.current.clearLayers?.();
        } catch {}
        clusterGroupRef.current = null;
      }
      if (mapInstanceRef.current) {
        try {
          if (typeof mapInstanceRef.current.remove === 'function') {
            mapInstanceRef.current.remove();
          } else if (typeof mapInstanceRef.current.destroy === 'function') {
            mapInstanceRef.current.destroy();
          }
        } catch {}
        mapInstanceRef.current = null;
      }
      if (mapRef.current) {
        try {
          delete mapRef.current._leaflet_id;
        } catch {}
      }
    };
  }, [destroyCurrentMap, isMapDarkMode, mapProvider, selectedRouteId, shouldIgnoreMapClick, showToast, spreadItems, syncCurrentLocationMarker, yandexApiKey]);

  useEffect(() => {
    if (!mapInstanceRef.current || !selectedRoute) return;
    moveMapToPoint([selectedRoute.markerLng, selectedRoute.markerLat], 160, MAP_ROUTE_FOCUS_ZOOM);
  }, [selectedRoute]);

  useEffect(() => {
    if (!onRegisterOpenDatePicker || normalizedFixedDateKey) return undefined;
    onRegisterOpenDatePicker(() => () => {
      haptic('open');
      setShowDatePicker(true);
    });
    return () => {
      onRegisterOpenDatePicker(() => () => {});
    };
  }, [normalizedFixedDateKey, onRegisterOpenDatePicker]);

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      const nextIsDark = root.classList.contains('dark');
      setIsGlobalDarkTheme(nextIsDark);
      if (!nextIsDark) {
        setIsMapDarkMode(false);
      }
    });

    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const nextFullscreen = document.fullscreenElement === mapWrapperRef.current;
      setIsFullscreen(nextFullscreen);

      window.requestAnimationFrame(() => {
        syncCurrentMapViewport();
      });
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const timers = [
      window.setTimeout(() => syncCurrentMapViewport(), 20),
      window.setTimeout(() => syncCurrentMapViewport(), 100),
      window.setTimeout(() => syncCurrentMapViewport(), 250),
      window.setTimeout(() => syncCurrentMapViewport(), 500)
    ];
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [isFullscreen]);

  useEffect(() => {
    if (!mapRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (mapEngineRef.current === 'leaflet' && mapInstanceRef.current) {
        syncCurrentMapViewport();
      }
    });
    ro.observe(mapRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => () => {
    if (document.fullscreenElement === mapWrapperRef.current) {
      document.exitFullscreen?.().catch?.(() => {});
    }
    markerRefs.current = [];
    mapThemeRef.current = null;
    if (mapInstanceRef.current) {
      if (mapEngineRef.current === 'leaflet') {
        mapInstanceRef.current.remove?.();
      } else {
        mapInstanceRef.current.destroy?.();
      }
      mapInstanceRef.current = null;
    }
  }, []);

  const orderedRoutes = useMemo(() => (
    [...spreadItems].sort((a, b) => {
      const draftCompare = Number(isDraftRouteItem(a)) - Number(isDraftRouteItem(b));
      if (draftCompare !== 0) return draftCompare;
      const dateCompare = String(a.scheduled_date_key || '').localeCompare(String(b.scheduled_date_key || ''));
      if (dateCompare !== 0) return dateCompare;
      return String(a.time_slot || '').localeCompare(String(b.time_slot || '')) || String(a.order_number || '').localeCompare(String(b.order_number || ''));
    })
  ), [spreadItems]);

  const todayKey = formatCalendarDateKey(new Date());

  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const firstDayOfWeek = firstDay.getDay() || 7;
    const days = [];

    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = firstDayOfWeek - 1; i > 0; i -= 1) {
      const date = new Date(year, month - 1, prevMonthDays - i + 1);
      days.push({
        key: formatCalendarDateKey(date),
        label: date.getDate(),
        isCurrentMonth: false
      });
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(year, month, day);
      const key = formatCalendarDateKey(date);
      days.push({
        key,
        label: day,
        isCurrentMonth: true,
        hasRoutes: dateCounts.has(key),
        isSelected: selectedDateKey === key,
        isToday: key === todayKey
      });
    }

    while (days.length < 42) {
      const day = days.length - (firstDayOfWeek - 1) - daysInMonth + 1;
      const date = new Date(year, month + 1, day);
      days.push({
        key: formatCalendarDateKey(date),
        label: date.getDate(),
        isCurrentMonth: false
      });
    }

    return days;
  }, [calendarMonth, dateCounts, selectedDateKey, todayKey]);

  const visibleStats = useMemo(() => {
    const stats = {
      total: visibleItems.length,
      unresolved: skippedItemsCount,
      statuses: {
        blue: 0,
        draft: 0,
        orange: 0,
        gray: 0,
        green: 0,
        red: 0
      },
      clients: {
        individual: 0,
        company: 0
      }
    };

    visibleItems.forEach((item) => {
      stats.statuses[getRouteStatusTone(item)] += 1;
      if (isDraftRouteItem(item)) return;
      stats.clients[getClientTypeKind(item.client_type)] += 1;
    });

    return stats;
  }, [skippedItemsCount, visibleItems]);

  const hasMountedMap = !!mapInstanceRef.current;
  const shouldShowInitialLoadingState = !mapError && !hasMountedMap && (loading || isMapBootstrapping || !!yandexApiKey);
  const shouldShowInitialEmptyState = !loading && visibleItems.length === 0 && !hasMountedMap;
  const shouldShowLoadingOverlay = loading && hasMountedMap;
  const shouldShowEmptyOverlay = !loading && visibleItems.length === 0 && hasMountedMap;
  const mapLoadingIndicator = (
    <div className="flex flex-col items-center justify-center gap-4 text-center">
      <div className="relative h-28 w-full max-w-[220px] overflow-hidden rounded-[24px] border border-blue-100 bg-gradient-to-br from-sky-50 via-white to-blue-50 shadow-sm dark:border-blue-900/60 dark:from-slate-900 dark:via-gray-900 dark:to-blue-950/40">
        <div className="absolute inset-0 opacity-70">
          <div className="absolute left-4 top-5 h-2 w-16 rounded-full bg-blue-100 dark:bg-blue-900/60" />
          <div className="absolute left-12 top-10 h-2 w-24 rounded-full bg-sky-100 dark:bg-sky-900/50" />
          <div className="absolute left-6 top-16 h-2 w-36 rounded-full bg-blue-100 dark:bg-blue-900/50" />
          <div className="absolute left-20 top-22 h-2 w-20 rounded-full bg-sky-100 dark:bg-sky-900/40" />
        </div>
        <motion.div
          className="absolute left-[44%] top-[42%] h-4 w-4 rounded-full bg-blue-500 shadow-[0_0_0_6px_rgba(59,130,246,0.16)] dark:bg-blue-400"
          animate={{ scale: [1, 1.12, 1], opacity: [0.9, 1, 0.9] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute left-[43.1%] top-[40.8%] h-6 w-6 rounded-full border-2 border-blue-300/70 dark:border-blue-500/60"
          animate={{ scale: [1, 1.35], opacity: [0.75, 0] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeOut' }}
        />
      </div>
      <div className="space-y-1">
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          Загружаем карту маршрутов
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Подтягиваем точки и подготавливаем слой карты...
        </div>
      </div>
    </div>
  );

  if (shouldShowInitialEmptyState) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-500 dark:bg-blue-950/30 dark:text-blue-300">
          <MapPinned className="h-6 w-6" />
        </div>
        На карте пока нечего показывать.
      </div>
    );
  }

  if (keyLoaded && !yandexApiKey) {
    return (
      <div className="rounded-2xl border border-dashed border-amber-300 bg-white p-8 text-center text-sm text-amber-700 shadow-sm dark:border-amber-800 dark:bg-gray-900 dark:text-amber-300">
        {mapError || 'Ключ Яндекс.Карт не настроен. Как только его добавят в управление системой, карта начнёт работать.'}
      </div>
    );
  }

  const selectedStageMeta = selectedRoute ? getStageMeta(selectedRoute) : null;
  const selectedRouteIsDraft = isDraftRouteItem(selectedRoute);
  const SelectedStageIcon = selectedStageMeta?.icon || Truck;
  const selectedAddress = selectedRoute
    ? (hideRegionInAddress ? hideRegionPrefix(selectedRoute.address_text || '') : (selectedRoute.address_text || ''))
    : '';
  const mapsUrl = selectedRoute ? (getYandexMapsUrl(selectedRoute.address_text || '') || getYandexAddressUrl(selectedRoute.address_text || '')) : null;
  const navigatorUrl = selectedRoute ? (getYandexNavigatorUrl(selectedRoute.address_text || '') || getYandexAddressUrl(selectedRoute.address_text || '')) : null;
  const dgisUrl = selectedRoute
    ? (getDgisRouteUrl({
        latitude: selectedRoute.markerLat ?? selectedRoute.latitude,
        longitude: selectedRoute.markerLng ?? selectedRoute.longitude,
        address: selectedRoute.address_text
      }) || getDgisAddressUrl(selectedRoute.address_text || ''))
    : null;
  const showItemsCount = !selectedRouteIsDraft && getRouteEntityType(selectedRoute) !== 'task' && selectedRoute?.stage_name === 'delivery' && Number(selectedRoute?.items_count || 0) > 0;
  const hasPrivilegedOpen = PRIVILEGED_TYPES.has(String(currentUserType || '').toLowerCase());
  const canOpenOrderDirectly = selectedRoute
    && !selectedRouteIsDraft
    && (
      Number(selectedRoute.assigned_to) === Number(currentUserId)
      || hasPrivilegedOpen
    );
  const isMapConfirmInsideFullscreen = Boolean(
    (isFullscreen || (typeof document !== 'undefined' && document.fullscreenElement === mapWrapperRef.current))
    && mapWrapperRef.current
  );

  const currentRouteKey = selectedRouteId || focusedRouteId;
  const currentRouteIndex = currentRouteKey ? orderedRoutes.findIndex((item) => getRouteKey(item) === currentRouteKey) : -1;
  const selectedDateLabel = selectedDateKey ? formatDateChip(selectedDateKey) : 'Дата';
  const todayHasRoutes = dateCounts.has(todayKey);
  const selectedRouteDateLabel = selectedRoute ? formatRouteDateLabel(selectedRoute.scheduled_date_key) : '';
  const selectedRouteStatusMeta = selectedRoute ? getRouteStatusMeta(selectedRoute) : { tone: 'blue', ...ROUTE_STATUS_META.blue };
  const selectedRouteShowStatusBadge = selectedRouteStatusMeta.tone !== 'blue';
  const selectedRouteHasCallRequirement = selectedRoute ? hasRouteCallRequirement(selectedRoute) : false;
  const selectedRouteHasEmergencyDispatch = Boolean(selectedRoute?.emergency_dispatch);
  const isClusterModalInsideFullscreen = Boolean(
    (isFullscreen || (typeof document !== 'undefined' && document.fullscreenElement === mapWrapperRef.current))
    && mapWrapperRef.current
  );

  const chooseClusterRoute = (item, event = null) => {
    if (!item) return;
    stopClusterSelectionEvent(event, true);
    haptic('tap');
    const routeKey = getRouteKey(item);
    suppressNextMapClick();
    setSelectedClusterRouteIds(null);
    setFocusedRouteId(routeKey);
    setSelectedRouteId(null);
    if (mapInstanceRef.current) {
      moveMapToPoint([item.markerLng, item.markerLat], 200, MAP_CLUSTER_SELECTION_ZOOM);
    }
    deferClusterRouteOpen(routeKey);
  };

  const goToNextRoute = () => {
    if (!orderedRoutes.length) return;
    haptic('tap');
    setSelectedClusterRouteIds(null);
    const shouldOpenCard = isFullscreen;
    if (currentRouteIndex === -1) {
      const firstRoute = orderedRoutes[0];
      const firstRouteKey = getRouteKey(firstRoute);
      setFocusedRouteId(firstRouteKey);
      if (shouldOpenCard) {
        setSelectedRouteId(firstRouteKey);
      } else if (mapInstanceRef.current) {
        setSelectedRouteId(null);
        moveMapToPoint([firstRoute.markerLng, firstRoute.markerLat], 160, MAP_ROUTE_FOCUS_ZOOM);
      }
      return;
    }
    const nextIndex = (currentRouteIndex + 1) % orderedRoutes.length;
    if (shouldOpenCard) {
      const nextRoute = orderedRoutes[nextIndex];
      const nextRouteKey = getRouteKey(nextRoute);
      setFocusedRouteId(nextRouteKey);
      setSelectedRouteId(nextRouteKey);
      return;
    }

    const nextRoute = orderedRoutes[nextIndex];
    const nextRouteKey = getRouteKey(nextRoute);
    setFocusedRouteId(nextRouteKey);
    setSelectedRouteId(null);
    if (mapInstanceRef.current) {
      moveMapToPoint([nextRoute.markerLng, nextRoute.markerLat], 160, MAP_ROUTE_FOCUS_ZOOM);
    }
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        haptic('open');
        await mapWrapperRef.current?.requestFullscreen?.();
      } else if (document.fullscreenElement === mapWrapperRef.current) {
        haptic('close');
        await document.exitFullscreen?.();
      }
    } catch (error) {
      console.error('Ошибка полноэкранного режима карты:', error);
    }
  };

  const openInternalRoute = async (event, href, options = {}) => {
    if (!href) return;
    event.preventDefault();
    const { routeContext = null } = options;

    if (routeContext) {
      saveRoutesNavigationContext(routeContext);
      prepareRoutesReturnHistory(routeContext.expandedDateKey || routeContext.selectedDateKey || '');
    }

    if (document.fullscreenElement === mapWrapperRef.current) {
      try {
        await document.exitFullscreen?.();
      } catch {
        // no-op
      }

      await new Promise((resolve) => {
        window.setTimeout(resolve, 80);
      });
    }

    if (href.startsWith('#')) {
      window.location.hash = href.slice(1);
      return;
    }

    window.location.assign(href);
  };

  const toggleMapDimension = () => {
    if (!isMap3DSupported || !mapInstanceRef.current || !mapLocationRef.current.center) return;
    const nextTilted = !isMapTilted;
    haptic('tap');
    mapInstanceRef.current.update({
      location: {
        center: mapLocationRef.current.center,
        zoom: mapLocationRef.current.zoom
      },
      camera: {
        azimuth: mapLocationRef.current.azimuth || 0,
        tilt: nextTilted ? MAP_3D_TILT : 0
      }
    });
    mapLocationRef.current = {
      ...mapLocationRef.current,
      azimuth: mapLocationRef.current.azimuth || 0,
      tilt: nextTilted ? MAP_3D_TILT : 0
    };
    setIsMapTilted(nextTilted);
  };

  const showCurrentLocation = (coordinates) => {
    if (!mapInstanceRef.current) return;
    currentLocationCoordsRef.current = coordinates;
    setSelectedRouteId(null);
    setSelectedClusterRouteIds(null);
    setFocusedRouteId(null);
    syncCurrentLocationMarker(coordinates);
    moveMapToPoint(coordinates, 250, CURRENT_LOCATION_MAP_ZOOM);
  };

  const showCurrentLocationError = (message) => {
    showToast(message, 'error');
  };

  const openRouteMapConfirm = (url, label) => {
    if (!url || url === '#') {
      showToast('Нет адреса для перехода', 'error');
      return;
    }
    haptic('open');
    setMapConfirmModal({
      url,
      label,
      address: selectedAddress || selectedRoute?.address_text || 'Адрес маршрута'
    });
  };

  const handleUnavailableOrderOpen = () => {
    haptic('error');
    showToast(getRouteUnavailableLabel(selectedRoute), 'error');
  };

  return (
    <div
      ref={mapWrapperRef}
      className={isFullscreen ? 'relative flex h-screen flex-col gap-3 bg-slate-100 p-3 dark:bg-gray-950' : 'relative space-y-3'}
    >
      <div className={`overflow-hidden rounded-[24px] border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900 ${isFullscreen ? 'flex min-h-0 flex-1 flex-col' : ''}`}>
        <div className="border-b border-gray-200 px-4 py-2.5 dark:border-gray-800">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Карта маршрутов</div>
            <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">{selectedDateLabel}</div>
          </div>
        </div>

        <div
          className={`route-map-canvas-container relative ${isMapDarkMode ? 'route-map-canvas--dark bg-gray-950' : 'bg-slate-100'} ${isFullscreen ? 'flex min-h-0 flex-1 flex-col' : 'h-[min(58dvh,480px)]'}`}
        >
          <div
            ref={mapRef}
            className="route-map-canvas h-full w-full"
          />

          {(shouldShowInitialLoadingState || !keyLoaded) && (
            <div className="absolute inset-0 z-[4] flex items-center justify-center bg-white/92 p-6 backdrop-blur-[1px] dark:bg-gray-950/92">
              <div className="w-full max-w-sm rounded-[24px] border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                {mapLoadingIndicator}
              </div>
            </div>
          )}

          {shouldShowLoadingOverlay && (
            <div className="pointer-events-none absolute inset-0 z-[3] flex items-center justify-center bg-white/45 p-6 backdrop-blur-[1px] dark:bg-gray-950/45">
              <div className="rounded-[24px] bg-white/95 px-5 py-4 shadow-sm dark:bg-gray-900/95">
                <div className="flex items-center gap-3">
                  <motion.div
                    className="h-3.5 w-3.5 rounded-full bg-blue-500 dark:bg-blue-400"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <div className="text-left">
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                      Обновляем маршруты
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Карта останется на месте, обновятся только точки
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {shouldShowEmptyOverlay && (
            <div className="pointer-events-none absolute inset-0 z-[2] flex items-center justify-center p-6">
              <div className="max-w-xs rounded-2xl bg-white/92 px-4 py-3 text-center text-sm text-gray-600 shadow-sm backdrop-blur dark:bg-gray-900/92 dark:text-gray-300">
                На карте пока нечего показывать.
              </div>
            </div>
          )}

          {visibleItems.length > 0 && geocodedItems.length === 0 && (
            <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center p-6">
              <div className="max-w-xs rounded-2xl bg-white/92 px-4 py-3 text-center text-sm text-gray-600 shadow-sm backdrop-blur dark:bg-gray-900/92 dark:text-gray-300">
                {isResolvingCoords
                  ? 'Уточняем координаты по адресам...'
                  : 'Для выбранной даты не удалось определить координаты адресов.'}
              </div>
            </div>
          )}

          {isFullscreen && (
            <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-start justify-between gap-2 p-3">
                <div className="pointer-events-auto flex items-center gap-2">
                  <MapProviderToggle
                    provider={mapProvider}
                    onChange={(nextProvider) => {
                      destroyCurrentMap();
                      setMapProvider(nextProvider);
                    }}
                  />
                  {normalizedFixedDateKey ? (
                    <div className="inline-flex h-10 items-center gap-2 rounded-2xl border border-gray-200 bg-white/95 px-3 text-xs font-semibold text-gray-700 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200">
                      <CalendarDays className="h-4 w-4" />
                      {selectedDateLabel}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        haptic('open');
                        setShowDatePicker(true);
                      }}
                      className="inline-flex h-10 items-center gap-2 rounded-2xl border border-gray-200 bg-white/95 px-3 text-xs font-semibold text-gray-700 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900"
                      title="Выбрать дату"
                      aria-label="Выбрать дату"
                    >
                      <CalendarDays className="h-4 w-4" />
                      {selectedDateLabel}
                    </button>
                  )}
                </div>

                <div className="pointer-events-auto flex items-start gap-2">
                  {isGlobalDarkTheme && (
                    <button
                      type="button"
                      onClick={() => {
                        haptic('tap');
                        setIsMapDarkMode((prev) => !prev);
                      }}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-700 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900"
                      title={isMapDarkMode ? 'Светлая карта' : 'Тёмная карта'}
                      aria-label={isMapDarkMode ? 'Светлая карта' : 'Тёмная карта'}
                    >
                      {isMapDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>
                  )}

                  <div className="flex flex-col items-end gap-2">
                    <button
                      type="button"
                      onClick={toggleFullscreen}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-700 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900"
                      title="Свернуть карту"
                      aria-label="Свернуть карту"
                    >
                      <Minimize2 className="h-4 w-4" />
                    </button>

                    <MapCurrentLocationButton
                      onLocated={showCurrentLocation}
                      onError={showCurrentLocationError}
                    />

                    <button
                      type="button"
                      onClick={toggleMapDimension}
                      disabled={!isMap3DSupported}
                      className={`inline-flex h-10 min-w-[48px] items-center justify-center rounded-2xl border border-gray-200 bg-white/95 px-2.5 text-xs font-semibold text-gray-700 shadow-sm backdrop-blur transition-colors dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 ${isMap3DSupported ? 'hover:bg-white dark:hover:bg-gray-900' : 'cursor-not-allowed opacity-50'}`}
                      title={isMap3DSupported ? (isMapTilted ? 'Переключить в 2D' : 'Переключить в 3D') : '3D недоступен для текущего ключа Яндекс.Карт'}
                      aria-label={isMap3DSupported ? (isMapTilted ? 'Переключить в 2D' : 'Переключить в 3D') : '3D недоступен для текущего ключа Яндекс.Карт'}
                    >
                      {isMapTilted ? '2D' : '3D'}
                    </button>
                  </div>
                </div>
            </div>
          )}

          {!isFullscreen && (
            <>
              <div className="pointer-events-none absolute left-3 top-3 z-20 flex items-center gap-2">
                <div className="pointer-events-auto">
                  <MapProviderToggle
                    provider={mapProvider}
                    onChange={(nextProvider) => {
                      destroyCurrentMap();
                      setMapProvider(nextProvider);
                    }}
                  />
                </div>
              </div>

              <div className="absolute right-3 top-3 z-20 flex items-start gap-2">
                {isGlobalDarkTheme && (
                  <button
                    type="button"
                    onClick={() => {
                      haptic('tap');
                      setIsMapDarkMode((prev) => !prev);
                    }}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-700 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900"
                    title={isMapDarkMode ? 'Светлая карта' : 'Тёмная карта'}
                    aria-label={isMapDarkMode ? 'Светлая карта' : 'Тёмная карта'}
                    >
                      {isMapDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>
                )}

                <div className="flex flex-col items-end gap-2">
                  <button
                    type="button"
                    onClick={toggleFullscreen}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-700 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900"
                    title="Развернуть карту"
                    aria-label="Развернуть карту"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </button>

                  <MapCurrentLocationButton
                    onLocated={showCurrentLocation}
                    onError={showCurrentLocationError}
                  />

                  <button
                    type="button"
                    onClick={toggleMapDimension}
                    disabled={!isMap3DSupported}
                    className={`inline-flex h-10 min-w-[48px] items-center justify-center rounded-2xl border border-gray-200 bg-white/95 px-2.5 text-xs font-semibold text-gray-700 shadow-sm backdrop-blur transition-colors dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 ${isMap3DSupported ? 'hover:bg-white dark:hover:bg-gray-900' : 'cursor-not-allowed opacity-50'}`}
                    title={isMap3DSupported ? (isMapTilted ? 'Переключить в 2D' : 'Переключить в 3D') : '3D недоступен для текущего ключа Яндекс.Карт'}
                    aria-label={isMap3DSupported ? (isMapTilted ? 'Переключить в 2D' : 'Переключить в 3D') : '3D недоступен для текущего ключа Яндекс.Карт'}
                  >
                    {isMapTilted ? '2D' : '3D'}
                  </button>
                </div>
            </div>
            </>
          )}

          {selectedRoute && (() => {
            const routeCardOverlay = (
            <div
              className={
                routeCardAsModal && !isFullscreen
                  ? 'modal-overlay-backdrop fixed inset-0 z-[70] flex items-end justify-center bg-black/50 px-3 pb-4 sm:items-center sm:pb-0'
              : 'pointer-events-none absolute inset-x-0 bottom-0 z-[30] p-3 sm:p-4'
              }
              onClick={
                routeCardAsModal && !isFullscreen
                  ? () => {
                      haptic('close');
                      setSelectedRouteId(null);
                    }
                  : undefined
              }
            >
                <div
                  className={`pointer-events-auto w-full max-w-xl rounded-[24px] border border-gray-200 bg-white p-4 shadow-[0_-8px_30px_rgba(15,23,42,0.14)] dark:border-gray-700 dark:bg-gray-900 ${
                    routeCardAsModal && !isFullscreen ? '' : 'mx-auto'
                  }`}
                  onClick={routeCardAsModal && !isFullscreen ? (event) => event.stopPropagation() : undefined}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex h-2.5 w-2.5 rounded-full ${
                            selectedRouteStatusMeta.dotClassName
                          }`}
                          aria-hidden="true"
                        />
                        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full border ${selectedStageMeta.badgeClassName}`}>
                          <SelectedStageIcon className="h-4 w-4" />
                        </span>
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700 dark:bg-gray-800 dark:text-gray-200">
                          {selectedRouteIsDraft ? getDraftRouteLabel(selectedRoute) : `№ ${selectedRoute.order_number}`}
                        </span>
                        {selectedRouteHasCallRequirement && (
                          <span
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800/70 dark:bg-blue-950/35 dark:text-blue-300"
                            title="Необходим созвон"
                            aria-label="Необходим созвон"
                          >
                            <PhoneCall className="h-3.5 w-3.5" />
                          </span>
                        )}
                        {selectedRouteHasEmergencyDispatch && (
                          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700 dark:border-fuchsia-800/70 dark:bg-fuchsia-950/35 dark:text-fuchsia-300" title="Экстренный выезд" aria-label="Экстренный выезд">
                            <Siren className="h-3.5 w-3.5" />
                          </span>
                        )}
                        {selectedRouteShowStatusBadge && (
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${getRouteStatusBadgeClassName(selectedRouteStatusMeta.tone)}`}>
                            {selectedRoute.lifecycle_label || selectedRouteStatusMeta.label}
                          </span>
                        )}
                        {showItemsCount && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
                            <ShoppingBasket className="h-3.5 w-3.5" />
                            {selectedRoute.items_count}
                          </span>
                        )}
                      </div>

                      <div className="mt-3 space-y-2 text-xs text-gray-500 dark:text-gray-400">
                        <div className="flex flex-wrap items-center gap-3">
                          {selectedRouteDateLabel && (
                            <span className="inline-flex items-center gap-1.5">
                              <CalendarDays className="h-3.5 w-3.5" />
                              {selectedRouteDateLabel}
                            </span>
                          )}
                          {!selectedRouteIsDraft && (
                            <span className="inline-flex items-center gap-1.5">
                              <Clock3 className="h-3.5 w-3.5" />
                              {formatTimeSlot(selectedRoute.time_slot) || 'Время не указано'}
                            </span>
                          )}
                        </div>

                        {!selectedRouteIsDraft && getRouteEntityType(selectedRoute) !== 'task' ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5" />
                              {selectedRoute.department_name || 'Подразделение не указано'}
                            </span>
                          </div>
                        ) : null}

                        {!selectedRouteIsDraft && (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`inline-flex items-center gap-1.5 ${
                              selectedRoute.assignee_name ? '' : 'rounded-full border border-slate-200 bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                            }`}>
                              <User2 className="h-3.5 w-3.5" />
                              {selectedRoute.assignee_name || 'Не назначен'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        haptic('tap');
                        setSelectedRouteId(null);
                      }}
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                      aria-label="Закрыть карточку маршрута"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-3 flex items-start gap-2 rounded-2xl bg-slate-50 px-3 py-3 text-sm text-gray-800 dark:bg-gray-800/80 dark:text-gray-100">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <span className="break-words leading-5">{selectedAddress || 'Адрес не указан'}</span>
                  </div>

                  {!selectedRouteIsDraft && (!hideRouteDetailsLink || !hideExternalNavigation) && (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    {!hideExternalNavigation && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openRouteMapConfirm(mapsUrl, 'Яндекс.Карты')}
                          className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-800 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                          title="Открыть в Яндекс.Картах"
                          aria-label="Открыть в Яндекс.Картах"
                        >
                          <img src={yaMapsIcon} alt="" className="h-5 w-5 object-contain" />
                        </button>

                        <button
                          type="button"
                          onClick={() => openRouteMapConfirm(navigatorUrl, 'Яндекс.Навигатор')}
                          className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-800 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                          title="Открыть в Яндекс.Навигаторе"
                          aria-label="Открыть в Яндекс.Навигаторе"
                        >
                          <img src={yandexNavigatorIcon} alt="" className="h-5 w-5 object-contain" />
                        </button>

                        {dgisUrl && (
                          <button
                            type="button"
                            onClick={() => openRouteMapConfirm(dgisUrl, '2ГИС')}
                            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-800 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                            title="Открыть в 2ГИС"
                            aria-label="Открыть в 2ГИС"
                          >
                            <img src={dgisIcon} alt="2ГИС" className="h-5 w-5 object-contain" />
                          </button>
                        )}
                      </div>
                    )}

                    {!hideRouteDetailsLink && (
                      <div className="flex items-center gap-2">
                        {canOpenOrderDirectly ? (
                          <a
                            href={getOpenRouteHref(selectedRoute)}
                            onClick={(event) => openInternalRoute(event, getOpenRouteHref(selectedRoute), {
                              routeContext: {
                                ...(routeContextBase || {}),
                                selectedDateKey,
                                focusOrderId: selectedRoute.order_id,
                                focusEntityType: getRouteEntityType(selectedRoute),
                                focusRouteKey: getRouteKey(selectedRoute)
                              }
                            })}
                            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700"
                            title={`Открыть ${getRouteTitleLabel(selectedRoute)}`}
                            aria-label={`Открыть ${getRouteTitleLabel(selectedRoute)}`}
                          >
                            <FileText className="h-4 w-4" />
                          </a>
                        ) : (
                          <button
                            type="button"
                            onClick={handleUnavailableOrderOpen}
                            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-500 dark:hover:bg-gray-700"
                            title={getRouteUnavailableLabel(selectedRoute)}
                            aria-label={getRouteUnavailableLabel(selectedRoute)}
                          >
                            <FileText className="h-4 w-4 opacity-55" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  )}
                </div>
            </div>
            );
            return routeCardAsModal && !isFullscreen
              ? ReactDOM.createPortal(routeCardOverlay, document.body)
              : routeCardOverlay;
          })()}
        </div>

        <div className={`flex items-center justify-between gap-3 border-t border-gray-200 px-4 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400 ${isFullscreen ? 'py-2.5' : 'py-3'}`}>
          <div className="min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-semibold text-gray-700 dark:text-gray-200">Всего: {visibleStats.total}</span>
            {Object.entries(visibleStats.statuses).map(([key, count]) => (
              count > 0 ? (
                <span
                  key={key}
                  className="inline-flex items-center gap-1"
                  title={ROUTE_STATUS_META[key].label}
                  aria-label={`${ROUTE_STATUS_META[key].label}: ${count}`}
                >
                  <span className={`h-2.5 w-2.5 rounded-full ${ROUTE_STATUS_META[key].dotClassName}`} />
                  <span>{count}</span>
                </span>
              ) : null
            ))}
            <span className="inline-flex items-center gap-1" title="Физические лица" aria-label={`Физические лица: ${visibleStats.clients.individual}`}>
              <User2 className="h-3.5 w-3.5" />
              <span>{visibleStats.clients.individual}</span>
            </span>
            <span className="inline-flex items-center gap-1" title="Юридические лица" aria-label={`Юридические лица: ${visibleStats.clients.company}`}>
              <Briefcase className="h-3.5 w-3.5" />
              <span>{visibleStats.clients.company}</span>
            </span>
            {visibleStats.unresolved > 0 ? (
              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-300" title="Без координат" aria-label={`Без координат: ${visibleStats.unresolved}`}>
                <AlertCircle className="h-3.5 w-3.5" />
                <span>{visibleStats.unresolved}</span>
              </span>
            ) : null}
            {currentRouteIndex !== -1 ? (
              <span className="text-[11px] font-semibold">
                Точка {Math.max(currentRouteIndex + 1, 1)} из {orderedRoutes.length}
              </span>
            ) : null}
          </div>
          {!isFullscreen && (
            <button
              type="button"
              onClick={goToNextRoute}
              disabled={!orderedRoutes.length}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:opacity-40"
              title="Следующая точка"
              aria-label="Следующая точка"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>

        {isFullscreen && (
          <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-800">
            <button
              type="button"
              onClick={goToNextRoute}
              disabled={!orderedRoutes.length}
              className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-40"
            >
              Следующая точка
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {mapError ? (
          <div className="border-t border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300">
            <span className="inline-flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {mapError}
            </span>
          </div>
        ) : null}
      </div>

      {selectedClusterRoutes.length > 0 && ReactDOM.createPortal(
        <div
          className={`modal-overlay-backdrop z-[65] flex items-end justify-center bg-black/50 px-3 pb-4 sm:items-center sm:pb-0 ${
            isClusterModalInsideFullscreen ? 'absolute inset-0' : 'fixed inset-0'
          }`}
          onClick={() => {
            haptic('close');
            setSelectedClusterRouteIds(null);
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            onClick={(event) => event.stopPropagation()}
            className="w-full max-w-lg rounded-[26px] bg-white p-4 shadow-2xl dark:bg-gray-900"
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-base font-semibold text-gray-900 dark:text-gray-100">Выберите точку маршрута</div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {selectedClusterRoutes.length} точек рядом на карте
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  haptic('close');
                  setSelectedClusterRouteIds(null);
                }}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                aria-label="Закрыть выбор точки"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[min(60dvh,420px)] space-y-2 overflow-y-auto pr-1">
              {selectedClusterRoutes.map((item) => {
                const clusterStageMeta = getStageMeta(item);
                const ClusterStageIcon = clusterStageMeta.icon || Truck;
                const clusterAddress = hideRegionInAddress ? hideRegionPrefix(item.address_text || '') : (item.address_text || '');
                const statusMeta = getRouteStatusMeta(item);
                const itemIsDraft = isDraftRouteItem(item);

                return (
                  <button
                    key={getRouteKey(item)}
                    type="button"
                    onPointerDownCapture={(event) => {
                      stopClusterSelectionEvent(event);
                      suppressNextMapClick();
                    }}
                    onClick={(event) => chooseClusterRoute(item, event)}
                    className="flex w-full items-start gap-3 rounded-2xl border border-gray-200 bg-white px-3 py-3 text-left transition-colors hover:bg-slate-50 dark:border-gray-800 dark:bg-gray-900 dark:hover:bg-gray-800"
                  >
                    <span className={`mt-1 inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${statusMeta.dotClassName}`} aria-hidden="true" />
                    <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${clusterStageMeta.badgeClassName}`}>
                      <ClusterStageIcon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {itemIsDraft ? getDraftRouteLabel(item) : getRouteTitleLabel(item) === 'поручение' ? 'Поручение' : `Заказ № ${item.order_number}`}
                        </span>
                        {!itemIsDraft && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                            <Clock3 className="h-3.5 w-3.5" />
                            {formatTimeSlot(item.time_slot) || 'Время не указано'}
                          </span>
                        )}
                        {hasRouteCallRequirement(item) && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 dark:bg-blue-950/35 dark:text-blue-300">
                            <PhoneCall className="h-3 w-3" />
                            Созвон
                          </span>
                        )}
                      </span>
                      <span className="mt-1 line-clamp-2 block text-xs leading-4 text-gray-500 dark:text-gray-400">
                        {clusterAddress || 'Адрес не указан'}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>,
        isClusterModalInsideFullscreen ? mapWrapperRef.current : document.body
      )}

      <AnimatePresence>
        {showDatePicker && !normalizedFixedDateKey && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay-backdrop fixed inset-0 z-50 flex items-end justify-center bg-black/50 px-3 pb-4 sm:items-center sm:pb-0"
            onClick={() => {
              haptic('close');
              setShowDatePicker(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.96 }}
              transition={{ duration: 0.18 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-[26px] bg-white p-4 shadow-2xl dark:bg-gray-900"
            >
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="text-base font-semibold text-gray-900 dark:text-gray-100">Дата на карте</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Показываем только даты, где есть маршруты</div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    haptic('close');
                    setShowDatePicker(false);
                  }}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mb-3 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    haptic('tap');
                    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
                  }}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {CALENDAR_MONTHS[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    haptic('tap');
                    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
                  }}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-gray-400 dark:text-gray-500">
                {CALENDAR_WEEKDAYS.map((day) => (
                  <div key={day} className="py-1">{day}</div>
                ))}
              </div>

              <div className="mt-2 grid grid-cols-7 gap-1">
                {calendarDays.map((day) => (
                  <button
                    key={day.key}
                    type="button"
                    disabled={!day.isCurrentMonth || !day.hasRoutes}
                    onClick={() => {
                      if (!day.hasRoutes) return;
                      haptic('tap');
                      setSelectedDateKey(day.key);
                      setSelectedRouteId(null);
                      setFocusedRouteId(null);
                      setSelectedClusterRouteIds(null);
                      setShowDatePicker(false);
                    }}
                    className={`relative flex h-10 items-center justify-center rounded-xl text-sm font-medium transition-colors ${
                      !day.isCurrentMonth
                        ? 'text-gray-300 dark:text-gray-700'
                        : day.isSelected
                          ? 'bg-blue-600 text-white'
                          : day.hasRoutes
                            ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300'
                            : 'text-gray-400 dark:text-gray-500'
                    } ${!day.hasRoutes ? 'cursor-default' : ''}`}
                  >
                    {day.label}
                    {day.hasRoutes && !day.isSelected && (
                      <span className="absolute bottom-1.5 h-1.5 w-1.5 rounded-full bg-blue-500" />
                    )}
                  </button>
                ))}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={!todayHasRoutes}
                  onClick={() => {
                    if (!todayHasRoutes) return;
                    haptic('tap');
                    setSelectedDateKey(todayKey);
                    setSelectedRouteId(null);
                    setFocusedRouteId(null);
                    setSelectedClusterRouteIds(null);
                    setShowDatePicker(false);
                  }}
                  className={`col-span-2 inline-flex min-h-[42px] items-center justify-center gap-2 rounded-2xl px-4 text-sm font-semibold ${
                    todayHasRoutes
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                  }`}
                >
                  <CalendarDays className="h-4 w-4" />
                  Сегодня
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {mapConfirmModal && (
        <ModalShell
          open={Boolean(mapConfirmModal)}
          onClose={() => {
            haptic('close');
            setMapConfirmModal(null);
          }}
          container={isMapConfirmInsideFullscreen && mapWrapperRef.current ? mapWrapperRef.current : undefined}
          zIndexClass={isMapConfirmInsideFullscreen ? 'z-[100]' : 'z-[70]'}
          variant="window"
          panelClassName="w-full max-w-md p-4"
        >
          <div className="mb-3 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 shadow-sm ring-1 ring-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-900/40">
              {mapConfirmModal.label === 'Яндекс.Навигатор' ? (
                <Navigation className="h-5 w-5" />
              ) : (
                <MapPin className="h-5 w-5" />
              )}
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Открыть {mapConfirmModal.label}?
              </h3>
              <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
                {mapConfirmModal.address || 'Адрес маршрута'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                haptic('close');
                setMapConfirmModal(null);
              }}
              className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => {
                haptic('open');
                window.open(mapConfirmModal.url, '_blank', 'noopener,noreferrer');
                setMapConfirmModal(null);
              }}
              className="min-h-[40px] flex-1 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
            >
              Открыть
            </button>
          </div>
        </ModalShell>
      )}

          </div>
  );
};

export default RoutesMap;
